import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, FieldError, useFieldArray, useForm } from 'react-hook-form'
import { CreateWorkbenchDto, HotLabEquipmentDto, UpdateWorkbenchDto, WasteContainerDto, WorkbenchDto } from '@services/dtos'
import { workbenchSchema } from '@utils/schemas'
import { useAppDispatch } from '@hooks/reduxHooks'
import { Button, FormHelperText, Grid, IconButton, Tooltip } from '@mui/material'
import { createWorkbench, updateWorkbench } from '@state/reducers/workbenchReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import InputField from '../inputs/InputField'
import { setStorageLocationFilters } from '@state/reducers/storageLocationReducer'
import { FormCrudHeader } from './FormCrudHeader'
import { AddWrapperInput } from '../inputs/AddWrapperInput'
import ServerAutocompleteMultiple from '../autocomplete/ServerAutocompleteMultiple'
import HotLabEquipmentForm from './HotLabEquipmentForm'
import { hotLabEquipmentService } from '@services/hotLabEquipmentService'
import WasteContainerForm from './WasteContainerForm'
import { wasteContainerService } from '@services/wasteContainerService'
import { useSelector } from '@state/store'
import { RoomType } from '@services/api'
import { findAllRooms } from '@state/thunks/roomThunk'
import { setHotLabEquipmentFilters } from '@state/reducers/hotLabEquipmentReducer'
import SelectField from '../inputs/SelectField'
import { Delete as DeleteIcon } from '@mui/icons-material'

type Props = {
  editingWorkbench?: WorkbenchDto | null
  onClose: () => void
}

const WorkbenchForm: React.FC<Props> = ({
  editingWorkbench,
  onClose}) => {
  const { handleSubmit, reset, control, watch, formState: { errors } } = useForm<CreateWorkbenchDto | UpdateWorkbenchDto>({
    defaultValues: editingWorkbench
      ? {
          code: editingWorkbench.code,
          name: editingWorkbench.name,
          hotLabEquipmentIds: editingWorkbench.hotLabEquipments?.map(({ id }) => id) ?? [],
          wasteContainerIds: editingWorkbench.wasteContainers?.map(({ id }) => id) ?? [],
          generatorSlots: editingWorkbench.generatorSlots?.map(({ id, name }) => ({ id, name })) ?? [],
          roomId: editingWorkbench.room.id,
        }
      : {
          hotLabEquipmentIds: [],
          wasteContainerIds: [],
          generatorSlots: [],
        },
    resolver: yupResolver(workbenchSchema),
  })
  const dispatch = useAppDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { rooms } = useSelector(({ room }) => ({
    rooms: room.rooms.filter((_room) => _room.type === RoomType.LabWorkstation),
  }))
  const hotLabEquipmentsCacheRef = React.useRef(new Map<string, HotLabEquipmentDto>())
  const wasteContainersCacheRef = React.useRef(new Map<string, WasteContainerDto>())
  const { fields: generatorSlots, append: appendGeneratorSlot, remove: removeGeneratorSlot } = useFieldArray({
    control,
    name: 'generatorSlots',
    keyName: 'rhfId',
  })

  useEffect(() => {
    if (editingWorkbench?.hotLabEquipments?.length) {
      for (const it of editingWorkbench.hotLabEquipments) {
        hotLabEquipmentsCacheRef.current.set(it.id, it)
      }
    }
    if (editingWorkbench?.wasteContainers?.length) {
      for (const it of editingWorkbench.wasteContainers) {
        wasteContainersCacheRef.current.set(it.id, it)
      }
    }
  }, [editingWorkbench])

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    dispatch(findAllRooms())
    return () => {
      reset()
      dispatch(setStorageLocationFilters({ search: '' }))
      dispatch(setHotLabEquipmentFilters({ search: '' }))
    }
  }, [dispatch])

  const fetchHotLabEquipments = async (search: string): Promise<HotLabEquipmentDto[]> => {
    const response = await hotLabEquipmentService.getAll({ search, 
      excludeAssignedToWorkbench: true, 
      excludeAssignedToWorkbenchExceptId: editingWorkbench?.id 
    })
    return response?.datas ?? []
  }
  const fetchWasteContainers = async (search: string): Promise<WasteContainerDto[]> => {
    const response = await wasteContainerService.getAll({
      search, excludeAssignedToWorkbench: true,
      excludeAssignedToWorkbenchExceptId: editingWorkbench?.id
    })
    return response?.datas ?? []
  }

  const onSubmitForm = async (data: CreateWorkbenchDto | UpdateWorkbenchDto) => {
    setIsSubmitting(true)

    if (editingWorkbench) {
      dispatch(updateWorkbench({ id: editingWorkbench.id, dto: data }))
        .unwrap()
        .then(() => {
          cb('L\'enceinte blindée a été modifiée avec succès')
          onClose()
        })
        .catch(() => cb('Une erreur est survenue'))
        .finally(() => setIsSubmitting(false))
    } else {
      dispatch(createWorkbench(data as CreateWorkbenchDto))
        .unwrap()
        .then(() => {
          cb('L\'enceinte blindée a été créée avec succès')
          onClose()
        })
        .catch(() => cb('Une erreur est survenue'))
        .finally(() => setIsSubmitting(false))
    }
  }

  const fields = [
    { name: 'code', label: 'Code', sm: 6 },
    { name: 'name', label: 'Nom' , sm: 6},
  ]

  const renderFields = (fields: any[]) => {
    return fields.map((field) => (
      <Grid item xs={12} sm={field.sm ?? 6} key={field.name}>
        <InputField
          control={control}
          name={field.name}
          label={field.label}
          type={field.type ?? 'text'}
          multiline={field.type === 'textarea'}
          rows={field.type === 'textarea' ? 2 : undefined}
          helperText={errors[field.name]?.message}
          error={!!errors[field.name]}
        />
      </Grid>
    ))
  }

  return (
    <div className="flex w-full h-full p-4">
      <form 
        onSubmit={(e) => {
          e.stopPropagation()
          handleSubmit(onSubmitForm)(e)
        }} 
        className="w-full">
        <FormCrudHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          editing={!!editingWorkbench}
        />

        <div className="max-w-screen-sm mx-auto mt-4">
          <fieldset className="border border-gray-200 p-4 rounded-lg">
            <legend className="text-lg font-bold mb-4 border border-gray-200 p-2 rounded-lg">
              Enceinte blindée
            </legend>

            <Grid container rowSpacing={2} columnSpacing={4} mt={1}>
              {renderFields(fields)}

              <Grid item xs={12}>
                <SelectField
                  control={control}
                  name={'roomId'}
                  options={rooms.map(({ id, label }) => ({
                    label,
                    value: id,
                  }))}
                  value={watch('roomId')}
                  label={'Salle'}
                />
              </Grid>

              <Grid item xs={12}>
                <AddWrapperInput
                  legend="Matériel de laboratoire"
                  addTooltipTitle="Ajouter du matériel"
                  dialogContent={(close) => (
                    <HotLabEquipmentForm
                      onClose={() => {
                        close()
                      }}
                    />
                  )}
                  input={<Controller
                    name="hotLabEquipmentIds"
                    control={control}
                    render={({ field: { value: ids = [], onChange } }) => {
                      const selected = (ids as string[])
                        .map((id) =>
                          hotLabEquipmentsCacheRef.current.get(id)
                          ?? editingWorkbench?.hotLabEquipments?.find(hotLabEquipment => hotLabEquipment.id === id)
                        )
                        .filter(Boolean) as HotLabEquipmentDto[]

                      return (
                        <ServerAutocompleteMultiple<HotLabEquipmentDto>
                          values={selected}
                          onChange={(hotLabEquipments) => {
                            hotLabEquipments.forEach((item) => {
                              hotLabEquipmentsCacheRef.current.set(item.id, item)
                            })
                            onChange(hotLabEquipments.map(({ id }) => id))
                          }}
                          fetchService={fetchHotLabEquipments}
                          getOptionLabel={({ name }) => name}
                          label="Matériel de laboratoire"
                          minSearchLength={1}
                          placeholder="Rechercher du matériel de laboratoire..."
                        />
                      )
                    }}
                  />}
                />
              </Grid>

              <Grid item xs={12}>
                <AddWrapperInput
                  legend="Conteneurs de déchets"
                  addTooltipTitle="Ajouter un conteneur de déchets"
                  dialogContent={(close) => (
                    <WasteContainerForm
                      onClose={() => {
                        close()
                      }}
                    />
                  )}
                  input={<Controller
                    name="wasteContainerIds"
                    control={control}
                    render={({ field: { value: ids = [], onChange } }) => {
                      const selected = (ids as string[])
                        .map((id) =>
                          wasteContainersCacheRef.current.get(id)
                          ?? editingWorkbench?.wasteContainers?.find(wasteContainer => wasteContainer.id === id)
                        )
                        .filter(Boolean) as WasteContainerDto[]

                      return (
                        <ServerAutocompleteMultiple<WasteContainerDto>
                          values={selected}
                          onChange={(wasteContainers) => {
                            wasteContainers.forEach((item) => {
                              wasteContainersCacheRef.current.set(item.id, item)
                            })
                            onChange(wasteContainers.map(({ id }) => id))
                          }}
                          fetchService={fetchWasteContainers}
                          getOptionLabel={({ label }) => label}
                          label="Conteneurs de déchets"
                          minSearchLength={1}
                          placeholder="Rechercher des conteneurs de déchets..."
                        />
                      )
                    }}
                  />}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    appendGeneratorSlot({ name: '' })
                  }}
                >
                  Ajouter un emplacement générateur
                </Button>
              </Grid>
              {(errors.generatorSlots as unknown as FieldError)?.message && (
                <Grid item xs={12}>
                  <FormHelperText error>
                    {(errors.generatorSlots as unknown as FieldError).message}
                  </FormHelperText>
                </Grid>
              )}
              {generatorSlots.map((field, index) => {
                const hasInstance = !!editingWorkbench?.generatorSlots
                  .find(({ id }) => id === (field as typeof field & { id?: string }).id)
                  ?.generatorInstance
                return (
                  <Grid item xs={12} key={field.rhfId}>
                    <fieldset className="border border-gray-200 p-2 rounded-lg">
                      <legend className="text-xs text-gray-500 font-medium mb-2 border border-gray-200 pl-3 rounded-lg">
                        Emplacement générateur {index + 1}
                        <Tooltip title={hasInstance ? 'Un générateur y est affecté, suppression impossible' : ''}>
                          <span>
                            <IconButton
                              onClick={() => removeGeneratorSlot(index)}
                              size='small'
                              disabled={hasInstance}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </legend>
                      <Grid container rowSpacing={2} columnSpacing={4}>
                        {renderFields([{ name: `generatorSlots.${index}.name`, label: 'Nom emplacement', sm: 12 }])}
                      </Grid>
                    </fieldset>
                  </Grid>
                )
              })}
            </Grid>
          </fieldset>
        </div>
      </form>
    </div>
  )
}

export default WorkbenchForm
